import React, { useEffect, useState } from 'react'
import { Outlet, useLocation, useNavigate, useOutletContext } from 'react-router-dom'
import { SectionName, Tab, TabsDiv } from '../../../components/styles/subheader-style'
import collectionsIcon from '../../../assets/home/shirt.svg'
import compositionIcon from '../../../assets/home/length.svg'
import funnelIcon from '../../../assets/home/production.svg'
import { TypeMainOutlet } from '..'
import { ViewModeSelector } from '../Pipelines/PipelineControls'
import ModuleKanban from '../Pipelines/ModuleKanban'
import { usePersistedFilters } from '../../../hooks/usePersistedFilters'
import { PageHeader } from '../../../components/StandardPage'
import { Header } from '../../../components/SubHeaderFilter/types'
import { collectionHeaders } from '../Collections'
import { useSearch } from '../../../contexts/SearchContext'

export const compositionProductHeaders: Header[] = [
  { key: 'productCode', label: 'Código', type: 'string' },
  { key: 'name', label: 'Produto', type: 'string' },
  { key: 'referenceProduct', label: 'Produto referência', type: 'string' },
  { key: 'description', label: 'Descrição', type: 'string' },
  { key: 'price', label: 'Valor', type: 'currency' },
  { key: 'graduationType', label: 'Tipo de graduação', type: 'string' },
  { key: 'supplierCode', label: 'Código do fornecedor', type: 'string' },
  { key: 'observations', label: 'Observações', type: 'string' },
  { key: 'clientIdentification', label: 'Identificação do cliente', type: 'string' },
  { key: 'composition', label: 'Composição', type: 'string' },
  { key: 'annotation', label: 'Anotação', type: 'string' },
  { key: 'approvals', label: 'Aprovações', type: 'string' },
  { key: 'measures', label: 'Medidas', type: 'string' },
  { key: 'colecao', label: 'Coleção', type: 'string' },
  { key: 'familia', label: 'Família', type: 'string' },
  { key: 'episodio', label: 'Episódio', type: 'string' },
  { key: 'grades', label: 'Grades', type: 'string' },
  { key: 'groups', label: 'Grupos', type: 'string' },
  { key: 'colors', label: 'Cores', type: 'string' },
  { key: 'step', label: 'Etapa', type: 'string' },
];

const COMPOSITION_VIEW_MODE_STORAGE_KEY = 'product-development-composition-view-mode'
type CompositionViewMode = 'board' | 'table'

const tabs = [
  { path: '/panel/collection', image: collectionsIcon, name: 'Coleção' },
  { path: '/panel/composition-product', image: compositionIcon, name: 'Ficha técnica de produto' },
  { path: '/panel/collection/funnel', image: funnelIcon, name: 'Funil' },
]

const ProductDevelopmentContent: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const outletContext = useOutletContext<TypeMainOutlet>()

  const collectionsFiltersState = usePersistedFilters('collections')
  const compositionFiltersState = usePersistedFilters('composition_product')

  const [compositionViewMode, setCompositionViewMode] = useState<CompositionViewMode>(() => {
    const persistedMode = localStorage.getItem(COMPOSITION_VIEW_MODE_STORAGE_KEY)
    return persistedMode === 'board' ? 'board' : 'table'
  })

  const isFormRoute =
    location.pathname.includes('/panel/collection/register') ||
    location.pathname.includes('/panel/collection/edit') ||
    location.pathname.includes('/panel/composition-product/create') ||
    location.pathname.includes('/panel/composition-product/edit')

  const isFunnelRoute = location.pathname.includes('/panel/collection/funnel')

  const activeTabIndex = (() => {
    if (location.pathname.includes('/panel/collection/funnel')) return 2
    if (location.pathname.startsWith('/panel/composition-product')) return 1
    return 0
  })()

  const activeTabName = activeTabIndex === 0 ? 'collection' : 'composition'

  const isCompositionListRoute =
    location.pathname.startsWith('/panel/composition-product') &&
    !location.pathname.includes('/panel/composition-product/create') &&
    !location.pathname.includes('/panel/composition-product/edit')

  const handleCompositionViewModeChange = (mode: CompositionViewMode) => {
    setCompositionViewMode(mode)
    localStorage.setItem(COMPOSITION_VIEW_MODE_STORAGE_KEY, mode)
    window.dispatchEvent(new CustomEvent('product-development-view-mode', { detail: { tab: 'composition', viewMode: mode } }))
  }

  const handleRefresh = () => {
    window.dispatchEvent(new CustomEvent('product-development-refresh', { detail: { tab: activeTabName } }))
  }

  const handleCreate = () => {
    if (activeTabName === 'collection') {
      navigate('/panel/collection/register')
      return
    }

    navigate('/panel/composition-product/create')
  }

  const createButtonLabel = activeTabName === 'collection' ? 'Nova coleção' : 'Novo produto'

  const activeFiltersState = activeTabName === 'collection' ? collectionsFiltersState : compositionFiltersState
  const activeFilterHeaders = activeTabName === 'collection' ? collectionHeaders : compositionProductHeaders
  const showFilterControls = activeTabName === 'collection' || isCompositionListRoute

  useEffect(() => {
    if (!isCompositionListRoute) {
      return
    }

    window.dispatchEvent(new CustomEvent('product-development-view-mode', {
      detail: { tab: 'composition', viewMode: compositionViewMode },
    }))
  }, [isCompositionListRoute, compositionViewMode])

  return (
    <>
      {!isFormRoute && !isFunnelRoute && (
        <PageHeader
          title="Desenvolvimento de Produto"
          tableName={activeTabName === 'collection' ? 'Collection' : 'Product'}
          moduleName={activeTabName === 'collection' ? 'Coleções' : 'Desenvolvimento de Produto'}
          tabs={tabs.map((tab) => ({ key: tab.path, label: tab.name, icon: tab.image }))}
          activeTab={tabs[activeTabIndex].path}
          onTabChange={(path) => {
            if (path !== location.pathname) {
              navigate(path)
            }
          }}
          onReload={handleRefresh}
          filterHeaders={showFilterControls ? activeFilterHeaders : undefined}
          appliedFilters={showFilterControls ? activeFiltersState.appliedFilters : undefined}
          onApplyFilter={showFilterControls ? activeFiltersState.handleApplyFilter : undefined}
          onRemoveFilter={showFilterControls ? activeFiltersState.handleRemoveFilter : undefined}
          onClearAllFilters={showFilterControls ? activeFiltersState.handleClearAll : undefined}
          createButton={{ label: createButtonLabel, onClick: handleCreate }}
          rightExtra={
            activeTabName === 'composition' && isCompositionListRoute ? (
              <ViewModeSelector
                value={compositionViewMode}
                onChange={(mode) => handleCompositionViewModeChange(mode as CompositionViewMode)}
                boardLabel='Quadro'
                tableLabel='Tabela'
              />
            ) : undefined
          }
        />
      )}
      <Outlet context={{
        ...outletContext,
        collectionsFiltersState,
        compositionFiltersState,
        appliedFilters: activeTabName === 'collection'
          ? collectionsFiltersState.appliedFilters
          : compositionFiltersState.appliedFilters
      }} />
    </>
  )
}

export default ProductDevelopmentContent

export const ProductDevelopmentFunnel: React.FC = () => {
  const { clearSearch } = useSearch()
  const navigate = useNavigate()
  const location = useLocation()

  const activeTabIndex = (() => {
    if (location.pathname.includes('/panel/collection/funnel')) return 2
    if (location.pathname.startsWith('/panel/composition-product')) return 1
    return 0
  })()

  return (
    <ModuleKanban
      moduleType="product_development"
      moduleName="Desenvolvimento de Produto"
      subModule="fashion"
      headerLeftContent={
        <>
          <SectionName>Desenvolvimento de Produto</SectionName>
          <TabsDiv>
            {tabs.map((tab, index) => (
              <Tab
                key={tab.path}
                $isFirst={index === 0}
                $isLast={index === tabs.length - 1}
                $isActive={index === activeTabIndex}
                onClick={() => {
                  navigate(tab.path)
                }}
              >
                <img src={tab.image} alt={`${tab.name}-icon`} />
                {tab.name}
              </Tab>
            ))}
          </TabsDiv>
        </>
      }
    />
  )
}
